import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { OrgNotificationProviderContext } from '../../internal'
import { OrgNotificationModel } from '../../../../models/notification/org-notification.model'
import { NotificationDto } from '../../../../features/notifications'
import { convertOrgNotificationModelToNotificationDto } from '../../../../features/organizations/helpers/convert-org-notification-model-to-notification-dto'
import { AbilityAction } from '../../../../enums/ability-action.enum'
import { asCaslSubject } from '../../../../stores/app-ability'
import { SubjectEntity } from '../../../../enums/ability-entity.enum'
import { useService } from '@pushly/aqe/lib/hooks'
import { AppState } from '../../../../stores/app'

type LoadedNotificationProviderApi = {
    isLoading(): boolean
    isLoaded(): boolean
    hasRequestedNotificationId(): boolean
    getRequestedNotificationId(): string | null
    getOrgNotification(): OrgNotificationModel | null
    getOrgNotificationPromise(): Promise<OrgNotificationModel | null>
    getNotifications(): NotificationDto[] | undefined
    getNotificationsPromise(): Promise<NotificationDto[] | undefined>
}

export enum OrgNotificationAuthZErrorReason {
    FORBIDDEN = 'forbidden',
}

type UseLoadedNotificationProviderApiOptions = {
    onAuthZError?: (reason: OrgNotificationAuthZErrorReason) => void
}

export function useLoadedOrgNotification(
    options: UseLoadedNotificationProviderApiOptions = {},
): LoadedNotificationProviderApi {
    const ctx = useContext(OrgNotificationProviderContext)
    if (!ctx) {
        throw new Error('useLoadedDomainNotificationTemplate must be used within a DomainNotificationTemplateProvider')
    }

    const { state } = ctx
    const appState = useService(AppState)

    const convertToNotificationDtoList = useCallback(
        (orgNotification: OrgNotificationModel | null) => {
            if (!orgNotification) return null

            const notifDto = convertOrgNotificationModelToNotificationDto(orgNotification)
            if (!notifDto) return null

            const domainIdsWhereCanCreateNotif = notifDto.audience.domainIds?.filter((domainId) =>
                appState.abilityStore.can(
                    AbilityAction.CREATE,
                    asCaslSubject(SubjectEntity.NOTIFICATION, { domainId }),
                ),
            )

            if (domainIdsWhereCanCreateNotif?.length !== notifDto.audience.domainIds?.length) {
                if (ctx.state.duplicateSourceId) {
                    // Filter domains during duplication and editing to only those editable by the current user
                    notifDto.audience.domainIds = domainIdsWhereCanCreateNotif
                } else {
                    options.onAuthZError?.(OrgNotificationAuthZErrorReason.FORBIDDEN)
                }
            }

            const groupNotifications = orgNotification.getNotifications()
            if (groupNotifications.length) {
                // if singular shared status - attach to working dto
                const statuses = Array.from(new Set(groupNotifications.map((n) => n.status)))
                if (statuses.length === 1) {
                    notifDto.status = statuses[0]
                }
            }

            return [notifDto]
        },
        [appState.abilityStore.acs.state],
    )

    const getOrgNotification = useCallback(() => {
        return state.notification
    }, [state.orgId, state.notificationId, state.notification])

    // workaround for existing use with PromisableProp
    const getOrgNotificationRef = useRef(getOrgNotification)
    useEffect(() => {
        getOrgNotificationRef.current = getOrgNotification
    }, [getOrgNotification])

    const loadingRef = useRef(state.loading)
    useEffect(() => {
        loadingRef.current = state.loading
    }, [state.loading])

    const getNotificationPromiseHasResponded = useRef(false)
    const getOrgNotificationPromise = useCallback(() => {
        return new Promise<OrgNotificationModel | null>((res, _rej) => {
            const fallbackTimer = setTimeout(() => {
                if (getNotificationPromiseHasResponded.current) return

                console.warn('getNotificationPromise timed out')

                getNotificationPromiseHasResponded.current = true
                clearTimeout(fallbackTimer)
                res(null)
            }, 30_0000) // max API Gateway response time

            const _getOrgNotification = () => {
                if (getNotificationPromiseHasResponded.current) {
                    return
                }

                if (loadingRef.current) {
                    setTimeout(_getOrgNotification, 100)
                    return
                }

                getNotificationPromiseHasResponded.current = true
                loadingRef.current = false
                clearTimeout(fallbackTimer)
                res(getOrgNotificationRef.current())
            }

            _getOrgNotification()
        })
    }, [])

    const getNotifications = useCallback(() => {
        return convertToNotificationDtoList(state.notification) ?? undefined
    }, [getOrgNotification])

    const getNotificationsPromise = useCallback(() => {
        return getOrgNotificationPromise().then((notif) => convertToNotificationDtoList(notif) ?? undefined)
    }, [getOrgNotificationPromise])

    const isLoaded = useCallback(() => !state.loading, [state.loading])
    const isLoading = useCallback(() => state.loading, [state.loading])
    const hasRequestedNotificationId = useCallback(() => !!state.notificationId, [state.notificationId])
    const getRequestedNotificationId = useCallback(() => state.notificationId, [state.notificationId])

    return useMemo(
        () => ({
            isLoaded,
            isLoading,
            hasRequestedNotificationId,
            getRequestedNotificationId,
            getOrgNotification,
            getOrgNotificationPromise,
            getNotifications,
            getNotificationsPromise,
        }),
        [
            isLoaded,
            isLoading,
            hasRequestedNotificationId,
            getRequestedNotificationId,
            getOrgNotification,
            getOrgNotificationPromise,
            getNotifications,
            getNotificationsPromise,
        ],
    )
}
